import React from 'react';

import { useMutation, useQuery } from '@betterment/js-runtime';
import { Box, Button, Flex, Text } from '@betterment/style-closet-react';

import Layout, { StyledCheckbox } from './Layout';
import LinkSent from './LinkSent';

import { CoPilotSectionProvider, useCoPilotSectionState } from '../../contexts/CoPilotSectionContext';
import { AccountApproval } from '../../services/api/types/co_pilot/account_approvals';
import { ActionRequestParams, CoPilotResponseMeta } from '../../utils/co_pilot';

const AccountApprovalRow: React.FC<React.PropsWithChildren<{ accountApproval: AccountApproval }>> = ({
  accountApproval: { id, clientName, accountType, linkSentAt, queuedAt },
}) => {
  const { isItemSelected, handleSelectedItemChange } = useCoPilotSectionState<AccountApproval>();

  return (
    <tr>
      <td>
        <Flex alignItems="center">
          <StyledCheckbox
            name={id}
            id={id}
            role="checkbox"
            type="checkbox"
            className="checkboxInputBase"
            checked={isItemSelected(id)}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => handleSelectedItemChange(target.id)}
          />
          {clientName}
        </Flex>
      </td>
      <td>{accountType}</td>
      <LinkSent linkSentAt={linkSentAt} queuedAt={queuedAt} />
    </tr>
  );
};

const AccountApprovalTable: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { itemList, status, handleSelectAllChange, lastPage, loadMore } = useCoPilotSectionState<AccountApproval>();
  return (
    <table>
      <tbody>
        <tr>
          <Text as="th" pb={6}>
            <Flex alignItems="center">
              <StyledCheckbox
                name="selectAll"
                id="selectAll"
                role="checkbox"
                type="checkbox"
                className="checkboxInputBase"
                checked={status === 'allSelected'}
                onChange={() => handleSelectAllChange()}
              />
              <Text fontWeight="medium">Client</Text>
            </Flex>
          </Text>
          <Text as="th" fontWeight="medium" pb={6}>
            Account
          </Text>
          <Text as="th" fontWeight="medium" pb={6}>
            Link sent
          </Text>
        </tr>
        {itemList?.map(accountApproval => (
          <AccountApprovalRow key={accountApproval.id} accountApproval={accountApproval} />
        ))}
        {status !== 'empty' && status !== 'fetchError' && !lastPage && (
          <tr>
            <td>
              <Box pb={9}>
                <Button variant="tertiary" onClick={() => loadMore()}>
                  Load more
                </Button>
              </Box>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

const AccountApprovals: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { refetch } = useQuery<AccountApproval[], CoPilotResponseMeta>(`/api/v1/co_pilot/account_approvals`, {
    skip: true,
  });
  const [postSendReminder] = useMutation<{}, ActionRequestParams>('/api/v1/co_pilot/account_approvals/reminders');
  const [postDismissal] = useMutation<{}, ActionRequestParams>('/api/v1/co_pilot/account_approvals/dismissals');

  return (
    <CoPilotSectionProvider
      fetch={refetch}
      initialOrder={[{ col: 'client_name', dir: 'desc' }]}
      postHandleDismissal={postDismissal}
      postSendReminder={postSendReminder}
    >
      <Layout
        activeTab="Account approvals"
        header="These clients have not yet approved the accounts you’ve opened for them."
        subHeader="Select the clients you’d like to send an email reminder to about opening their account. We’ll make sure to copy you on the email."
      >
        <AccountApprovalTable />
      </Layout>
    </CoPilotSectionProvider>
  );
};

export default AccountApprovals;
